import * as React from "react";

import { useGetExpiredContractsReportQuery } from "@/api/spend";
import { downloadCsv, toCsv } from "@/helpers/csv";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { ExpiredContractRow } from "@/types/spend";
import { GraceControl } from "./components/grace-control";
import { NewBadge } from "./components/new-badge";
import { ReportLayout } from "./components/report-layout";
import { ReportStatus } from "./components/report-status";
import type { ReportColumn } from "./components/report-table";
import { REPORT_REFETCH_SECONDS, amount, creditNotes, gbp, matchesSearch, monthParam } from "./report-utils";
import type { ReportViewProps } from "./types";

const statusLabel = (status: string | null) => (status ? status[0].toUpperCase() + status.slice(1) : "Not set");

interface Props extends ReportViewProps {
  graceDays: number;
  onGraceDaysChange: (graceDays: number) => void;
}

export const ExpiredContractsReportView = ({ state, onStateChange, graceDays, onGraceDaysChange }: Props) => {
  const { formatDate, formatMonthYear } = useDateFormatter();
  const { data, isLoading, isFetching, isError, refetch } = useGetExpiredContractsReportQuery(
    { month: state.month ? monthParam(state.month) : undefined, grace_days: graceDays || undefined },
    { refetchOnMountOrArgChange: REPORT_REFETCH_SECONDS },
  );
  const rows = React.useMemo(
    () => (data?.rows ?? []).filter(row => matchesSearch(state.search, [row.contract_id, ...row.folder_names])),
    [data?.rows, state.search],
  );

  if (isLoading || isError || !data) return <ReportStatus isLoading={isLoading} onRetry={refetch} />;

  const { summary, previous_summary: previous, exclusions } = data;
  const previousLabel = formatMonthYear(previous.month);
  const columns: ReportColumn<ExpiredContractRow>[] = [
    {
      header: "Contract ID",
      cell: row => (
        <span className="font-semibold">
          {row.contract_id}
          {row.is_new ? <NewBadge /> : null}
        </span>
      ),
    },
    { header: "Folder name", cell: row => row.folder_names.join(", ") || "—" },
    { header: "Status", cell: row => row.statuses.map(statusLabel).join(", ") },
    { header: "Expiry date", cell: row => formatDate(row.expiry_date) },
    { header: "Months since expiry", numeric: true, cell: row => row.months_since_expiry },
    { header: "Txns", numeric: true, cell: row => row.transaction_count },
    {
      header: "Spend this month",
      numeric: true,
      cell: row => <strong>{gbp(row.spend_gbp)}</strong>,
      total: gbp(summary.spend_gbp),
    },
    {
      header: "Credit notes",
      numeric: true,
      cell: row => creditNotes(row.credits_gbp, row.credit_count),
      total: creditNotes(summary.credits_gbp, summary.credit_count),
    },
    { header: "Cumulative since expiry", numeric: true, cell: row => gbp(row.cumulative_gbp) },
    { header: "First month", cell: row => formatMonthYear(row.first_month) },
  ];
  const exportCsv = (shown: ExpiredContractRow[]) =>
    downloadCsv(
      toCsv([
        [
          "Month",
          "Contract ID",
          "Folder name(s)",
          "Status(es)",
          "Expiry date",
          "Months since expiry",
          "Transactions",
          "Spend GBP",
          "Credit notes",
          "Credit notes GBP",
          "Cumulative since expiry GBP",
          "First month with spend",
          "New this month",
        ],
        ...shown.map(row => [
          monthParam(data.month),
          row.contract_id,
          row.folder_names.join("; "),
          row.statuses.map(statusLabel).join("; "),
          row.expiry_date,
          amount(row.months_since_expiry),
          row.transaction_count,
          amount(row.spend_gbp),
          row.credit_count,
          amount(row.credits_gbp),
          amount(row.cumulative_gbp),
          monthParam(row.first_month),
          row.is_new ? "Yes" : "No",
        ]),
      ]),
      `spend-on-expired-contracts-${monthParam(data.month)}.csv`,
    );

  return (
    <ReportLayout
      state={state}
      onStateChange={onStateChange}
      data={data}
      isFetching={isFetching}
      rows={rows}
      searchLabel="Search contract ID or folder name"
      extraControls={<GraceControl value={graceDays} onChange={onGraceDaysChange} />}
      notes={
        <p>
          Excluded from this report: {exclusions.shared_with_active} IDs shared with an active contract ·{" "}
          {exclusions.all_active} where every contract is active · {exclusions.no_expiry} with no expiry date ·{" "}
          {exclusions.open_ended} open-ended.
        </p>
      }
      cards={[
        {
          title: "Spend after expiry",
          value: gbp(summary.spend_gbp),
          detail: `${previousLabel}: ${gbp(previous.spend_gbp)}`,
        },
        {
          title: "Contracts with spend",
          value: String(summary.id_count),
          detail: `${previousLabel}: ${previous.id_count}`,
        },
        {
          title: "Transactions",
          value: String(summary.transaction_count),
          detail: `${previousLabel}: ${previous.transaction_count}`,
        },
        {
          title: "Newly appearing",
          value: String(summary.new_id_count),
          detail: `${gbp(summary.new_spend_gbp)} this month · ${previousLabel}: ${previous.new_id_count}`,
        },
      ]}
      chart={{
        title: "Spend after expiry by month",
        subtitle: "Exclusively non-active contracts, GBP",
        idLabel: "Contracts",
      }}
      table={{
        label: "Spend on expired contracts",
        columns,
        rowKey: row => row.contract_id,
        totalLabel: `Month total, ${summary.id_count} contracts`,
        emptyTitle: "No spend on expired contracts",
      }}
      onExport={exportCsv}
    />
  );
};
